export const REPORT_CONFIGURATION_NAME = "cactus-risk-studio";

export const INITIAL_INPAGE_ROW_COUNT = 10;

export const DEFAULT_METRICS_COLLAPSED = false;

export const UI_PREFS_STORAGE_KEY = "reportsV3_uiPrefs";
// Per-user, per-browser saved report layouts (keyed by reportId). Used by the
// embedded dashboard report's "Save my layout" so a user's columns/filters/sort
// /grouping/summary persist across reloads without a server-side saved report.
export const LOCAL_LAYOUT_STORAGE_KEY = "reportsV3_localLayout";
// Per-user, per-browser id of the report last opened in Report Builder, so
// the landing route reopens it instead of running a default report.
export const LAST_OPENED_REPORT_STORAGE_KEY = "reportsV3_lastOpenedReport";
export const NUMERIC_AGGREGATE_METHODS = ["MIN", "MAX", "SUM", "AVG", "MEDIAN"];
export const DATE_AGGREGATE_METHODS = ["MIN", "MAX", "MEDIAN"];
export const COLUMN_DATA_TYPES = [
  "text",
  "option",
  "date",
  "binary",
  "unit",
  "currency",
  "percentage",
  "aggregate",
  "groupAggregate",
];

// Plain names for the column data types, as shown to users.
export const COLUMN_DATA_TYPE_NAMES = {
  text: "text",
  option: "choice",
  date: "date",
  binary: "yes/no",
  unit: "number",
  currency: "amount",
  percentage: "percentage",
  aggregate: "formula",
  groupAggregate: "group summary",
};

// Filter-spec vocabulary. The bound sets list every function name a saved spec
// may carry for that bound; the singular names are the ones newly built specs
// use.
export const BLANK_VALUE = "<blank>";
export const BOOL_COLUMN_TYPE = "BOOL";
export const IN_FILTER_FUNCTION = "IN";
export const IS_TRUE_FILTER_FUNCTION = "IS_TRUE";
export const IS_FALSE_FILTER_FUNCTION = "IS_FALSE";
export const LOWER_BOUND_FILTER_FUNCTION = "GREATER_THAN_OR_EQUAL";
export const UPPER_BOUND_FILTER_FUNCTION = "LESS_THAN_OR_EQUAL";
export const LOWER_BOUND_FILTER_FUNCTIONS = [
  "GREATER_THAN_OR_EQUAL",
  "GREATER_THAN",
  "AFTER",
];
export const UPPER_BOUND_FILTER_FUNCTIONS = [
  "LESS_THAN_OR_EQUAL",
  "LESS_THAN",
  "BEFORE",
];

// Why the builder's Save and Create buttons are disabled, reported in this
// order so the user is told about one thing at a time.
export const SAVE_BLOCKED_NO_COLUMNS = "SAVE_BLOCKED_NO_COLUMNS";
export const SAVE_BLOCKED_JOINS_INCOMPLETE = "SAVE_BLOCKED_JOINS_INCOMPLETE";
export const SAVE_BLOCKED_NAME_MISSING = "SAVE_BLOCKED_NAME_MISSING";
export const SAVE_BLOCKED_NAME_NOT_UNIQUE = "SAVE_BLOCKED_NAME_NOT_UNIQUE";

// Grouping-column naming, row types and limits for grouping by numeric ranges.
export const GROUPING_COLUMN_PREFIX = "~~Grouping-Column~~";
export const GROUPING_COLUMN_SUFFIX = "~~";
export const GROUP_ROW_DETAIL = "Detail";
export const GROUP_ROW_GROUP = "Group";
export const GROUP_ROW_GRAND_TOTAL = "Grand Total";
export const GROUP_ROW_SUB_TOTAL = "Sub Total";

// Grid cell classes for numeric text values and per-row alignment overrides.
export const NUMERIC_TEXT_VALUE_CLASS = "numeric-text-value";
export const CELL_ALIGN_CLASS_PREFIX = "cell-align-";
export const DEFAULT_CELL_ALIGN = "left";
export const SUMMARY_ROW = "Summary";
export const RANGE_GROUPING_TWO_DIGIT_LIMIT = 98;
export const RANGE_GROUPING_MAX_GROUPS = 998;
export const RANGE_GROUPING_LABEL_MAX_FRACTION_DIGITS = 3;
export const AUTO_FLOAT_MAX_FRACTION_DIGITS = 3;
export const AUTO_CURRENCY_MIN_FRACTION_DIGITS = 2;
export const AUTO_CURRENCY_MAX_FRACTION_DIGITS = 3;
export const PERCENTAGE_DEFAULT_FRACTION_DIGITS = 1;
export const FLOAT_DEFAULT_FRACTION_DIGITS = 2;
export const TARGET_PERCENTAGE_MAX_FRACTION_DIGITS = 2;
export const RANGE_ROW_ERROR_NOT_A_NUMBER = "RANGE_ROW_ERROR_NOT_A_NUMBER";
export const RANGE_ROW_ERROR_NOT_ASCENDING = "RANGE_ROW_ERROR_NOT_ASCENDING";
export const RANGE_ROW_ERROR_NOT_A_DATE = "RANGE_ROW_ERROR_NOT_A_DATE";
export const RANGE_VALUE_TYPE_NUMBER = "number";
export const RANGE_VALUE_TYPE_DATE = "date";
export const RANGE_VALUE_TYPE_STRING = "string";
export const STRING_MATCHER_FUNCTION = "STRING_MATCHER";
export const STRING_MATCHER_RE_FUNCTION = "STRING_MATCHER_RE";
export const RANGE_MATCH_REGEX_DELIMITER = "/";
export const RANGE_GROUPING_DATE_FIELD_MIN_WIDTH = "12em";
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const RANGE_GROUPING_ENTRY_INDENT = "20px";
export const RANGE_GROUPING_LABEL_MIN_HEIGHT = "1.5em";
export const RANGE_GROUPING_LABEL_EDITOR_MIN_WIDTH = "16em";
export const RANGE_GROUPING_ORDINAL_PREFIX_PATTERN = /^\d{2,}: /;
export const RANGE_NULL_TARGET_NO_VALUE = "noValue";
export const RANGE_NULL_TARGET_FIRST = "first";
export const RANGE_NULL_TARGET_LAST = "last";
export const CIRCLE_TOGGLE_ACTIVE_COLOR = "success.main";
export const CIRCLE_TOGGLE_INACTIVE_COLOR = "grey.300";
export const CIRCLE_TOGGLE_ICON_SIZE = "1.35rem";
export const CIRCLE_TOGGLE_PADDING = "1px";

// Group header dropdown icon sizing.
export const GROUP_HEADER_DROPDOWN_ICON_SIZE = "2rem";
export const GROUP_HEADER_SELECT_ICON_PADDING = GROUP_HEADER_DROPDOWN_ICON_SIZE;

// Input (pre-aggregation) and output (post-aggregation) filter kinds on the results page.
export const FILTER_KIND_INPUT = "input";
export const FILTER_KIND_OUTPUT = "output";
export const INPUT_COLUMN_FILTER_CHANGE_TYPE = "inputColumnFilter";
export const INPUT_DATE_FILTER_CHANGE_TYPE = "inputDateFilter";
export const INPUT_NUMBER_FILTER_CHANGE_TYPE = "inputNumberFilter";
export const FILTER_KIND_LABEL_SEPARATOR = " — ";
export const SUBMENU_INDICATOR = "▸";
export const HEADER_SUBMENU_CLOSE_DELAY_MS = 150;
export const HEADER_MENU_Z_INDEX = 1300;
export const HEADER_SUBMENU_Z_INDEX = 1301;
export const HEADER_MENU_MIN_WIDTH_PX = 150;
export const HEADER_SUBMENU_MIN_WIDTH_PX = 120;
export const HEADER_SUMMARY_SUBMENU_MIN_WIDTH_PX = 180;
export const HEADER_MENU_ICON_MIN_WIDTH_PX = 28;
export const ENUM_COLUMN_TYPE = "ENUM";
export const STRING_COLUMN_TYPE = "STRING";
export const ENUM_BLANK_IF_NOT_MAPPED_SUB_TYPE = "ENUM_BLANK_IF_NOT_MAPPED";
export const ENUM_BUTTONS_SUB_TYPE = "ENUM_BUTTONS";
export const ENUM_STATUS_BUTTONS_SUB_TYPE = "ENUM_STATUS_BUTTONS";
export const PERCENTAGE_FROM_FRACTION_SUB_TYPE = "PERCENTAGE_FROM_FRACTION";
export const PERCENTAGE_FROM_FRACTION_MULTIPLIER = 100;
export const CURRENCY_SUB_TYPE = "CURRENCY";
export const PERCENTAGE_SUB_TYPE = "PERCENTAGE";
export const TARGET_PERCENTAGE_SUB_TYPE = "TARGET_PERCENTAGE";
export const PERCENTAGE_SUB_TYPES = [
  PERCENTAGE_SUB_TYPE,
  TARGET_PERCENTAGE_SUB_TYPE,
  PERCENTAGE_FROM_FRACTION_SUB_TYPE,
];
export const CURRENCY_SYMBOL = "$";
export const PERCENT_SYMBOL = "%";
export const SCALED_FILTER_VALUE_SIGNIFICANT_DIGITS = 12;
export const DEFAULT_COLUMN_WIDTH_PX = 150;
export const DEFAULT_EXTRA_COLUMN_WIDTH_PX = 100;
export const COLUMN_WIDTH_SLIGHT_CHANGE_PERCENT = 2;

// Expression editor operand pairing types, type families and pass-through functions.
export const PAIRING_TYPE_SAME = "SAME";
export const PAIRING_TYPE_DIFFERENT = "DIFFERENT";
export const PAIRING_TYPE_ANY = "ANY";
export const TYPE_FAMILIES = {
  NUMBER: ["INT", "FLOAT"],
  DATE: ["DATE"],
  STRING: ["STRING"],
  ENUM: [ENUM_COLUMN_TYPE],
  BOOL: ["BOOL"],
};
export const PASS_THROUGH_FUNCTIONS = ["CASE", "ALL_OR_NONE"];
export const FRACTION_TO_PERCENTAGE_FUNCTION = "FRACTION_TO_PERCENTAGE";
// Comparison functions whose number literal is shown with the other side's sub-type marker, and entered as a percentage against a PERCENTAGE_FROM_FRACTION value.
export const LITERAL_COMPARISON_FUNCTIONS = ["EQ", "GT", "GTE", "LT", "LTE"];

export const BINARY_DATA_TYPE = "binary";
export const SUMMARISING_OUTPUT_TYPE = "SUMMARISING";
export const FILTER_MAP_KEYS = ["value", "date", "number"];
// Filters button column value meaning "show the column selector".
export const FILTER_SELECT_COLUMN = "__SELECT__";
export const FILTER_VALUES_LOADER_SIZE_PX = 24;

// Specification keys that do not change a SUMMARISING run's result.
export const SUMMARY_INDEPENDENT_SPEC_KEYS = [
  "filters",
  "preAggregationFilters",
  "order",
  "summaryRow",
];

// Marks the wrapper of each value field in the range grouping dialog.
export const RANGE_VALUE_ENTRY_ATTRIBUTE = "data-range-value-entry";

// Filter popover and panel sizing.
export const FILTERS_POPOVER_MAX_HEIGHT_PX = 480;
export const FILTERS_POPOVER_MAX_WIDTH = "calc(100vw - 32px)";
export const FILTERS_ACTIVE_LIST_MAX_HEIGHT_PX = 360;
export const DATE_FILTER_INPUT_WIDTH = "11em";
export const NUMERIC_FILTER_INPUT_WIDTH = "calc(12ch + 25px)";
export const NUMERIC_FILTER_HISTOGRAM_HEIGHT_PX = 48;
export const NUMERIC_FILTER_MIN_BAR_HEIGHT_PX = 2;
export const NUMERIC_FILTER_SLIDER_RELEASE_DELAY_MS = 100;
export const SEARCHABLE_LIST_FLEX = "1 1 200px";
export const SEARCHABLE_LIST_MIN_HEIGHT_PX = 100;
export const SEARCHABLE_LIST_PAGE_SIZE = 100;
export const SEARCHABLE_LIST_PAGINATION_THRESHOLD = 200;
export const SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX = 1;

// MUI Menu onClose reason for a click outside the menu.
export const MENU_CLOSE_REASON_BACKDROP = "backdropClick";
